import React, {useEffect, useState} from 'react';
import {Table, Card, message, Row, Col, DatePicker, Space} from 'antd';
import axios from 'axios';
import {CommonAdminUrl} from '../../services/serviceEndpoints.js';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {userGroupsPostFix} from '../../utils/commons.js';
import {useUserContext} from '../../utils/UserProvider.jsx';
const {RangePicker} = DatePicker;

const ClickStatisticsPage = ({accessToken}) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupedDailyRequests, setGroupedDailyRequests] = useState(null);
  const [userStatDateRange, setUserStatDateRange] = useState([]);
  const [filteredUserStats, setFilteredUserStats] = useState([]);
  const [dailyRequestDateRange, setDailyRequestDateRange] = useState([]);
  const [filteredDailyRequests, setFilteredDailyRequests] = useState([]);
  const [isModerator, setIsModerator] = useState();
  const {userName, userGroups} = useUserContext();

  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);

  useEffect(() => {
    const isModerator = userGroups?.some(group =>
      group.includes(
        userGroupsPostFix.Moderator || userGroupsPostFix.ExternalModerator
      )
    );
    setIsModerator(isModerator);
  }, [userGroups, userName]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (!dailyRequestDateRange || dailyRequestDateRange.length === 0) {
      setFilteredDailyRequests(groupedDailyRequests || []);
      return;
    }

    const [start, end] = dailyRequestDateRange;

    const filtered = (groupedDailyRequests || []).filter(item => {
      const requestDate = dayjs(item.RequestDate);
      return (
        requestDate.isSameOrAfter(start, 'day') &&
        requestDate.isSameOrBefore(end, 'day')
      );
    });

    setFilteredDailyRequests(filtered);
  }, [dailyRequestDateRange, groupedDailyRequests]);

  useEffect(() => {
    if (!userStatDateRange || userStatDateRange.length === 0) {
      setFilteredUserStats(statistics?.UserStatistic || []);
      return;
    }

    const [start, end] = userStatDateRange;

    const filtered = (statistics?.UserStatistic || []).filter(item => {
      const requestDate = dayjs(item.RequestDate);
      return (
        requestDate.isSameOrAfter(start, 'day') &&
        requestDate.isSameOrBefore(end, 'day')
      );
    });

    setFilteredUserStats(filtered);
  }, [userStatDateRange, statistics]);

  const fetchStatistics = async () => {
    try {
      setLoading(true); // Ensure loading state is set

      const response = await axios.get(`${CommonAdminUrl.GetClickStatistic}`, {
        headers: {Authorization: `Bearer ${accessToken}`},
      });
      setStatistics(response.data);

      let res = response.data?.DailyRequestLog
        ? Object.values(
            response.data.DailyRequestLog.reduce((acc, log) => {
              let dateKey = formatDate(log.RequestDate);

              if (!acc[dateKey]) {
                acc[dateKey] = {
                  RequestDate: log.RequestDate,
                  TotalRequests: 0,
                  InternalUsers: 0,
                  ExternalUsers: 0,
                };
              }

              acc[dateKey].TotalRequests += log.TotalRequests;

              if (log.InternalUser === true) {
                acc[dateKey].InternalUsers += log.TotalRequests;
              } else if (log.InternalUser === false) {
                acc[dateKey].ExternalUsers += log.TotalRequests;
              }
              return acc;
            }, {})
          )
        : [];

      setGroupedDailyRequests(res);
    } catch (error) {
      message.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = date => (date ? dayjs(date).format('DD-MM-YYYY') : 'N/A');

  const tableProps = {
    pagination: {pageSize: 20},
    loading,
    size: 'small',
    bordered: true,
    scroll: {y: 400},
  };

  const dailyRequestColumns = [
    {
      title: 'Date',
      dataIndex: 'RequestDate',
      key: 'requestDate',
      render: formatDate,
    },
    {
      title: 'Total Requests',
      dataIndex: 'TotalRequests',
      key: 'totalRequests',
    },
    {
      title: 'External Users',
      dataIndex: 'ExternalUsers',
      key: 'externalUsers',
    },
    {
      title: 'Internal Users',
      dataIndex: 'InternalUsers',
      key: 'internalUsers',
    },
  ];

  const userStatisticColumns = [
    {
      title: 'User Name',
      dataIndex: 'UserName',
      key: 'userName',
      filters: statistics?.UserStatistic
        ? [...new Set(statistics.UserStatistic.map(u => u.UserName))].map(
            name => ({text: name, value: name})
          )
        : [],
      filterSearch: true,
      onFilter: (value, record) => record.UserName === value,
    },
    {
      title: 'Request Date',
      dataIndex: 'RequestDate',
      key: 'requestDate',
      render: formatDate,
    },
    {
      title: 'Total Requests',
      dataIndex: 'TotalRequests',
      key: 'totalRequests',
    },
  ];

  const filterStatisticColumns = [
    {
      title: 'Country Code',
      dataIndex: 'DocumentClassification',
      key: 'countryCode',
      render: c => (c ? c.CountryCode || '-' : '-'),
      filters: statistics?.FilterStatistic
        ? [
            ...new Set(
              statistics.FilterStatistic.map(
                f => f.DocumentClassification?.CountryCode
              )
            ),
          ]
            .filter(Boolean)
            .map(code => ({text: code, value: code}))
        : [],
      filterSearch: true,
      onFilter: (value, record) =>
        record.DocumentClassification?.CountryCode === value,
    },
    {
      title: 'Document Type',
      dataIndex: 'DocumentClassification',
      key: 'documentType',
      render: c => (c ? c.DocumentType || '-' : '-'),
      filters: statistics?.FilterStatistic
        ? [
            ...new Set(
              statistics.FilterStatistic.map(
                f => f.DocumentClassification?.DocumentType
              )
            ),
          ]
            .filter(Boolean)
            .map(type => ({text: type, value: type}))
        : [],
      filterSearch: true,
      onFilter: (value, record) =>
        record.DocumentClassification?.DocumentType === value,
    },
    {
      title: 'State',
      dataIndex: 'DocumentClassification',
      key: 'state',
      render: c => (c ? c.State || '-' : '-'),
      filters: statistics?.FilterStatistic
        ? [
            ...new Set(
              statistics.FilterStatistic.map(
                f => f.DocumentClassification?.State
              )
            ),
          ]
            .filter(Boolean)
            .map(state => ({text: state, value: state}))
        : [],
      filterSearch: true,
      onFilter: (value, record) =>
        record.DocumentClassification?.State === value,
    },
    {
      title: 'Version',
      dataIndex: 'DocumentClassification',
      key: 'version',
      render: c => (c ? c.DocumentVersion || '-' : '-'),
      filters: statistics?.FilterStatistic
        ? [
            ...new Set(
              statistics.FilterStatistic.map(
                f => f.DocumentClassification?.DocumentVersion
              )
            ),
          ]
            .filter(Boolean)
            .map(v => ({text: v, value: v}))
        : [],
      filterSearch: true,
      onFilter: (value, record) =>
        record.DocumentClassification?.DocumentVersion === value,
    },
    {
      title: 'Last Requested At',
      dataIndex: 'LastRequestedAt',
      key: 'lastRequestedAt',
      render: formatDate,
    },
    {
      title: 'Total Requests',
      dataIndex: 'TotalRequests',
      key: 'totalRequests',
    },
  ];

  const documentRequestColumns = [
    {
      title: 'Classification',
      dataIndex: 'ClassificationStr',
      key: 'classificationStr',
      filters: statistics?.DocumentRequestLog
        ? [
            ...new Set(
              statistics.DocumentRequestLog.map(d => d.ClassificationStr)
            ),
          ].map(name => ({text: name, value: name}))
        : [],
      filterSearch: true,
      onFilter: (value, record) => record.ClassificationStr === value,
    },
    {
      title: 'LastRequestAt',
      dataIndex: 'LastRequestAt',
      key: 'lastRequestAt',
      render: formatDate,
    },
    {
      title: 'Total Requests',
      dataIndex: 'TotalRequests',
      key: 'totalRequests',
    },
  ];

  if (!isModerator) {
    return <div>No Access</div>;
  }

  return (
    <div style={{padding: 20}}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space direction="vertical">
                <span>Daily Requests</span>
                <RangePicker
                  format="DD-MM-YYYY"
                  onChange={dates => setDailyRequestDateRange(dates)}
                />
              </Space>
            }
          >
            <Table
              {...tableProps}
              dataSource={filteredDailyRequests}
              columns={dailyRequestColumns}
              rowKey={record => record.Id || record.RequestDate}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space direction="vertical">
                <span>User Statistics</span>
                <RangePicker
                  format="DD-MM-YYYY"
                  onChange={dates => setUserStatDateRange(dates)}
                />
              </Space>
            }
          >
            <Table
              {...tableProps}
              dataSource={filteredUserStats}
              columns={userStatisticColumns}
              rowKey={record => record.UserName || record.Id}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{marginTop: 20}}>
        <Col span={10}>
          <Card title="Document Requests">
            <Table
              {...tableProps}
              dataSource={statistics?.DocumentRequestLog || []}
              columns={documentRequestColumns}
              rowKey={record => record.documentId || record.id}
            />
          </Card>
        </Col>
        <Col span={14}>
          <Card title="Filter Statistics">
            <Table
              {...tableProps}
              dataSource={statistics?.FilterStatistic || []}
              columns={filterStatisticColumns}
              rowKey={record => record.Id || record.LastRequestedAt}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClickStatisticsPage;
